import React, { useEffect, useState, useRef } from 'react';
import { XIcon, UserIcon, CalendarIcon, ClipboardIcon, ClockIcon, PencilIcon, TrashIcon, ExternalLinkIcon, CheckIcon, XCircleIcon, AlertCircleIcon, RepeatIcon } from 'lucide-react';
import { getTaskDisplayStatus } from './taskUtils';
import { localISOToUtcISO, LOCAL_TZ } from '../../src/utils/time';
import { ymdLocal } from '../../src/utils/datetime';
import { deleteRecurringTasks, updateRecurringTasks } from '../../services/taskService';
import { toast } from 'react-hot-toast';
// Task types
type TaskStatus = 'pending' | 'in-progress' | 'completed';
interface AssignedTask {
  id: string;
  name?: string;
  title?: string;
  studentId: string;
  studentName: string;
  studentAvatarUrl?: string;
  deadline?: string;
  dueDate?: string;
  dueAt?: string;
  status: TaskStatus;
  notes?: string;
  createdAt?: string;
  reference?: string;
  startDate?: string;
  isRecurring?: boolean;
  recurringGroupId?: string | null;
  recurrencePattern?: {
    frequency: string;
    days: number[];
    endDate: string;
  } | null;
}
interface TaskDetailsPanelProps {
  open: boolean;
  task?: AssignedTask | null;
  onClose: () => void;
  onEditClick?: (taskId: string) => void;
  onDeleteClick?: (taskId: string) => void;
  onSaveEdit?: (taskId: string, updates: {
    title: string;
    dueAt?: string | null;
    status: 'pending' | 'in-progress' | 'completed';
    notes?: string | null;
    reference?: string | null;
  }) => void;
  darkMode: boolean;
  isEditMode?: boolean;
  onExitEditMode?: () => void;
  teacherId?: string;
}
export const TaskDetailsPanel: React.FC<TaskDetailsPanelProps> = ({
  open,
  task,
  onClose,
  onEditClick,
  onDeleteClick,
  onSaveEdit,
  darkMode,
  isEditMode = false,
  onExitEditMode,
  teacherId
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [animation, setAnimation] = useState<'entering' | 'entered' | 'exiting' | 'exited'>('exited');
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedReason, setEditedReason] = useState('');
  const [editedDetails, setEditedDetails] = useState('');
  const [editedPracticeHours, setEditedPracticeHours] = useState<number | undefined>(undefined);
  // Local state for displayed values (updates after save)
  const [displayedReason, setDisplayedReason] = useState('');
  const [displayedDetails, setDisplayedDetails] = useState('');
  const [displayedPracticeHours, setDisplayedPracticeHours] = useState<number | undefined>(undefined);
  const lastActiveElement = useRef<HTMLElement | null>(null);
  // State for copy feedback
  const [isCopied, setIsCopied] = useState(false);
  // Remove local isEditMode state - now using prop from parent
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [recurringDeleteScope, setRecurringDeleteScope] = useState<'single' | 'future' | 'all' | null>(null);
  const [recurringEditScope, setRecurringEditScope] = useState<'single' | 'future' | 'all' | null>(null);
  // Form values for edit mode
  const [formValues, setFormValues] = useState({
    title: '',
    dueDate: '',
    dueTime: '',
    status: 'pending' as TaskStatus,
    notes: '',
    reference: ''
  });
  // Track if form has unsaved changes
  const [isDirty, setIsDirty] = useState(false);
  // Validation state
  const [errors, setErrors] = useState<{
    title?: string;
    dueDate?: string;
  }>({});
  // Handle animation states
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (open && animation !== 'entered') {
      // Save the currently focused element to restore focus later
      lastActiveElement.current = document.activeElement as HTMLElement;
      setAnimation('entering');
      timer = setTimeout(() => {
        setAnimation('entered');
      }, 50); // Small delay to ensure the entering class is applied
    } else if (!open && animation !== 'exited') {
      setAnimation('exiting');
      timer = setTimeout(() => {
        setAnimation('exited');
      }, 300); // Match the CSS transition duration
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [open, animation]);
  // Reset when panel closes
  useEffect(() => {
    if (!open) {
      setShowDiscardConfirm(false);
      setShowDeleteConfirm(false);
      setIsDirty(false);
      setRecurringDeleteScope(null);
      setRecurringEditScope(null);
    }
  }, [open]);
  // Initialize form values when task changes or edit mode is entered
  useEffect(() => {
    if (task && isEditMode) {
      // Split date and time from ISO string for separate inputs
      let dueDate = '';
      let dueTime = '';
      const dueAt = task.dueAt || task.deadline || task.dueDate;
      if (dueAt) {
        try {
          // Use ymdLocal to extract the date in local timezone
          dueDate = ymdLocal(dueAt, LOCAL_TZ);
          // For time, convert to local date
          const date = new Date(dueAt);
          if (!isNaN(date.getTime())) {
            dueTime = date.toTimeString().slice(0, 5);
          }
        } catch (err) {
          console.error('Error parsing due date:', err);
        }
      }
      setFormValues({
        title: task.title || task.name || '',
        dueDate,
        dueTime,
        status: task.status || 'pending',
        notes: task.notes || '',
        reference: task.reference || ''
      });
      setIsDirty(false);
      setErrors({});
    }
  }, [task, isEditMode]);
  // Handle loading state
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (open) {
      setIsLoading(true);
      timer = setTimeout(() => {
        setIsLoading(task === undefined || task === null);
      }, 200); // Show loading state if task is still null after 200ms
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [open, task]);
  // Handle escape key press
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        // If delete confirmation is showing, close it
        if (showDeleteConfirm) {
          setShowDeleteConfirm(false);
          return;
        }
        if (isEditMode && isDirty) {
          // Show confirmation dialog if there are unsaved changes
          setShowDiscardConfirm(true);
        } else if (isEditMode) {
          // Exit edit mode if no changes
          setIsEditMode(false);
        } else {
          // Close panel if not in edit mode
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [open, onClose, isEditMode, isDirty, showDeleteConfirm]);
  // Handle Enter key for delete confirmation modal
  useEffect(() => {
    const handleEnterKey = (event: KeyboardEvent) => {
      // Only handle Enter when delete confirmation modal is open
      if (event.key === 'Enter' && showDeleteConfirm) {
        // Prevent if user is typing in an input field (shouldn't be any in this modal, but safety check)
        const target = event.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          return;
        }
        event.preventDefault();
        handleDeleteConfirm();
      }
    };
    document.addEventListener('keydown', handleEnterKey);
    return () => {
      document.removeEventListener('keydown', handleEnterKey);
    };
  }, [showDeleteConfirm, task]);
  // Restore focus when panel closes
  useEffect(() => {
    if (!open && lastActiveElement.current) {
      setTimeout(() => {
        lastActiveElement.current?.focus();
      }, 300); // Match the CSS transition duration
    }
  }, [open]);
  // Focus trap inside the panel
  useEffect(() => {
    if (!open || !panelRef.current) return;
    const panel = panelRef.current;
    const focusableElements = panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusableElements.length === 0) return;
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
    // Focus the first element when panel opens
    if (animation === 'entered' && !isEditMode) {
      firstElement.focus();
    }
    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          event.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          event.preventDefault();
        }
      }
    };
    panel.addEventListener('keydown', handleTabKey);
    return () => {
      panel.removeEventListener('keydown', handleTabKey);
    };
  }, [open, animation, isEditMode]);
  // If panel is not open or exited, don't render anything
  if (animation === 'exited' && !open) return null;
  // Enter edit mode - now only calls parent callback
  const handleEnterEditMode = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only call parent callback to enter edit mode
    if (onEditClick && task) {
      onEditClick(task.id);
    }
  };
  // Cancel edit mode
  const handleCancelEdit = () => {
    if (isDirty) {
      setShowDiscardConfirm(true);
    } else {
      // Exit edit mode via parent callback
      if (onExitEditMode) {
        onExitEditMode();
      } else {
        // Fallback: close the panel
        onClose();
      }
    }
  };
  // Confirm discard changes
  const handleConfirmDiscard = () => {
    setShowDiscardConfirm(false);
    setIsDirty(false);
    // Exit edit mode via parent callback
    if (onExitEditMode) {
      onExitEditMode();
    } else {
      // Fallback: close the panel
      onClose();
    }
  };
  // Handle form input changes
  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
    setIsDirty(true);
    // Clear validation error when field is updated
    if (name === 'title' && errors.title) {
      setErrors(prev => ({
        ...prev,
        title: undefined
      }));
    }
  };
  // Validate the form before saving
  const validateForm = () => {
    const newErrors: {
      title?: string;
      dueDate?: string;
    } = {};
    if (!formValues.title.trim()) {
      newErrors.title = 'Title is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Handle form submission
  const handleSaveChanges = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !task) return;
    // If recurring task and scope not yet chosen, show scope modal
    if (task.isRecurring && task.recurringGroupId && !recurringEditScope) {
      setRecurringEditScope('single'); // pre-select first option
      return;
    }
    // Pass the raw YYYY-MM-DD date string directly.
    // The updateTask function in taskService.ts already handles YYYY-MM-DD
    // by treating it as local midnight — no UTC conversion needed here.
    const dueAt: string | null = formValues.dueDate && formValues.dueDate.trim() ? formValues.dueDate.trim() : null;
    const updates = {
      title: formValues.title,
      dueAt,
      status: formValues.status,
      notes: formValues.notes || null,
      reference: formValues.reference || null
    };
    // Handle recurring edit scope
    if (task.isRecurring && task.recurringGroupId && recurringEditScope && recurringEditScope !== 'single') {
      // Batch update for future or all
      handleRecurringEditConfirm(updates);
      return;
    }
    // Single task update (default or 'single' scope)
    if (onSaveEdit) {
      onSaveEdit(task.id, updates);
    }
    // Exit edit mode via parent callback
    setIsDirty(false);
    setRecurringEditScope(null);
    if (onExitEditMode) {
      onExitEditMode();
    }
  };
  // Handle recurring edit batch update
  const handleRecurringEditConfirm = async (updates: {
    title: string;
    dueAt?: string | null;
    status: 'pending' | 'in-progress' | 'completed';
    notes?: string | null;
    reference?: string | null;
  }) => {
    if (!task || !teacherId || !task.recurringGroupId || !recurringEditScope) return;
    try {
      setIsSaving(true);
      // Build safe update payload — strip dueAt since we never overwrite dates in batch
      const batchUpdates: Record<string, unknown> = {
        title: updates.title,
        status: updates.status
      };
      if (updates.notes !== undefined) batchUpdates.notes = updates.notes;
      if (updates.reference !== undefined) batchUpdates.reference = updates.reference;
      const {
        count
      } = await updateRecurringTasks(teacherId, task.recurringGroupId, recurringEditScope as 'future' | 'all', task.dueDate || task.deadline || task.dueAt, batchUpdates);
      toast.success(`Updated ${count} recurring tasks`);
      window.dispatchEvent(new Event('taskUpdated'));
      setIsDirty(false);
      setRecurringEditScope(null);
      if (onExitEditMode) {
        onExitEditMode();
      }
    } catch (error) {
      console.error('Error updating recurring tasks:', error);
      toast.error('Failed to update recurring tasks');
    } finally {
      setIsSaving(false);
    }
  };
  // Handle copy Task ID to clipboard
  const handleCopyTaskId = async () => {
    if (!task?.id) return;
    try {
      await navigator.clipboard.writeText(task.id);
      setIsCopied(true);
      // Reset after 2 seconds
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy Task ID:', err);
    }
  };
  // Format date for display
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  // Format time for display
  const formatTime = (dateStr?: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Format date and time together
  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '—';
    // Only show the date, not the time
    return formatDate(dateStr);
  };
  // Get status badge style based on status - UPDATED to use computed status
  const getStatusBadge = (status?: TaskStatus, task?: AssignedTask | null) => {
    // If we have the full task object, use computed status
    if (task) {
      const displayStatus = getTaskDisplayStatus(task);
      switch (displayStatus) {
        case 'completed':
          return darkMode ? 'bg-green-900/30 text-green-200' : 'bg-green-100 text-green-800';
        case 'overdue':
          return darkMode ? 'bg-red-900/30 text-red-200' : 'bg-red-100 text-red-800';
        case 'in-progress':
          return darkMode ? 'bg-yellow-900/30 text-yellow-200' : 'bg-yellow-100 text-yellow-800';
        case 'pending':
        default:
          return darkMode ? 'bg-blue-900/30 text-blue-200' : 'bg-blue-100 text-blue-800';
      }
    }
    // Fallback to original logic if we only have status string
    switch (status) {
      case 'completed':
        return darkMode ? 'bg-green-900/30 text-green-200' : 'bg-green-100 text-green-800';
      case 'in-progress':
        return darkMode ? 'bg-yellow-900/30 text-yellow-200' : 'bg-yellow-100 text-yellow-800';
      case 'pending':
      default:
        return darkMode ? 'bg-blue-900/30 text-blue-200' : 'bg-blue-100 text-blue-800';
    }
  };
  // Get human-readable status text - UPDATED to use computed status
  const getStatusText = (status?: TaskStatus, task?: AssignedTask | null) => {
    // If we have the full task object, use computed status
    if (task) {
      const displayStatus = getTaskDisplayStatus(task);
      switch (displayStatus) {
        case 'completed':
          return 'Completed';
        case 'overdue':
          return 'Overdue';
        case 'in-progress':
          return 'In Progress';
        case 'pending':
        default:
          return 'Pending';
      }
    }
    // Fallback to original logic if we only have status string
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in-progress':
        return 'In Progress';
      case 'pending':
      default:
        return 'Pending';
    }
  };
  // Get status icon color based on computed status
  const getStatusIconColor = (task?: AssignedTask | null) => {
    if (!task) return '#9CA3AF'; // Default gray
    const displayStatus = getTaskDisplayStatus(task);
    switch (displayStatus) {
      case 'completed':
        return '#22C55E';
      // Green
      case 'overdue':
        return '#EF4444';
      // Red
      case 'in-progress':
        return '#EAB308';
      // Yellow
      case 'pending':
      default:
        return '#3B82F6';
      // Blue
    }
  };
  // Check if task is overdue
  const isOverdue = (dateStr?: string) => {
    if (!dateStr) return false;
    const dueDate = new Date(dateStr);
    const now = new Date();
    return dueDate < now && task?.status !== 'completed';
  };
  // Get the correct due date from various possible fields
  const getDueDate = () => {
    return task?.dueAt || task?.deadline || task?.dueDate;
  };
  // Prevent closing panel when clicking on the panel itself
  const handlePanelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (task?.isRecurring && recurringDeleteScope && recurringDeleteScope !== 'single') {
      if (!teacherId || !task.recurringGroupId) {
        toast.error('Missing information to delete recurring tasks');
        return;
      }
      try {
        const {
          count
        } = await deleteRecurringTasks(teacherId, task.recurringGroupId, recurringDeleteScope, task.dueDate || task.deadline || task.dueAt);
        toast.success(`Deleted ${count} recurring tasks`);
        window.dispatchEvent(new Event('taskDeleted'));
        onClose();
      } catch (error) {
        console.error('Error deleting recurring tasks:', error);
        toast.error('Failed to delete recurring tasks');
      }
    } else {
      if (onDeleteClick && task) {
        onDeleteClick(task.id);
      }
    }
    setShowDeleteConfirm(false);
    setRecurringDeleteScope(null);
  };
  return <div className={`fixed inset-0 z-50 ${animation === 'exited' ? 'pointer-events-none' : ''}`} aria-modal="true" role="dialog" aria-labelledby="task-details-title" data-id="element-12021">
      {/* Overlay - Fixed to only close when clicked directly */}
      <div className={`absolute inset-0 bg-black transition-opacity duration-300 ${animation === 'entering' || animation === 'entered' ? 'opacity-50' : 'opacity-0'}`} onClick={e => {
      // Only close if clicked directly on the overlay (not when event bubbles up)
      if (e.target === e.currentTarget) {
        if (isEditMode && isDirty) {
          setShowDiscardConfirm(true);
        } else if (!isEditMode) {
          onClose();
        }
      }
    }} data-id="element-12022" />
      {/* Panel */}
      <div ref={panelRef} className={`absolute inset-y-0 right-0 w-full max-w-[480px] ${darkMode ? 'bg-navy-800 text-white' : 'bg-white text-gray-800'} shadow-xl flex flex-col transform transition-transform duration-500 ${animation === 'entering' || animation === 'entered' ? 'translate-x-0' : 'translate-x-full'} md:rounded-l-2xl`} onClick={handlePanelClick} data-id="element-12023">
        {/* Panel Header */}
        <div className={`px-6 py-4 flex items-center justify-between border-b ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-12024">
          <h2 id="task-details-title" className="text-xl font-semibold truncate flex-1 pr-4" data-id="element-12025">
            {isLoading ? <div className={`h-7 w-3/4 rounded ${darkMode ? 'bg-navy-700' : 'bg-gray-200'} animate-pulse`} data-id="element-12026"></div> : isEditMode ? 'Edit Task' : task?.title || task?.name || 'Task Details'}
          </h2>
          <button type="button" onClick={e => {
          e.stopPropagation();
          if (isEditMode && isDirty) {
            setShowDiscardConfirm(true);
          } else {
            onClose();
          }
        }} className={`p-2 rounded-full ${darkMode ? 'hover:bg-navy-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'} transition-colors`} aria-label="Close panel" data-id="element-12027">
            <XIcon className="h-5 w-5" data-id="element-12028" />
          </button>
        </div>
        {/* Discard Changes Confirmation */}
        {showDiscardConfirm && <div className="p-4 mx-6 mt-6 rounded-lg bg-red-500/10 border border-red-500/30" data-id="element-12029">
            <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-800'} mb-3`} data-id="element-12030">
              <AlertCircleIcon className="inline-block h-4 w-4 mr-2 text-red-500" data-id="element-12031" />
              Discard your unsaved changes?
            </p>
            <div className="flex justify-end space-x-3" data-id="element-12032">
              <button type="button" onClick={e => {
            e.stopPropagation();
            setShowDiscardConfirm(false);
          }} className={`px-3 py-1.5 rounded-lg text-sm ${darkMode ? 'bg-navy-700 text-white hover:bg-navy-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`} data-id="element-12033">
                Cancel
              </button>
              <button type="button" onClick={e => {
            e.stopPropagation();
            handleConfirmDiscard();
          }} className="px-3 py-1.5 rounded-lg text-sm bg-red-600 text-white hover:bg-red-700" data-id="element-12034">
                Discard
              </button>
            </div>
          </div>}
        {/* Panel Content */}
        <div className="flex-1 overflow-y-auto" data-id="element-12035">
          {isLoading ? <div className="p-6 space-y-6" data-id="element-12036">
              <div className={`h-12 rounded ${darkMode ? 'bg-navy-700' : 'bg-gray-200'} animate-pulse`} data-id="element-12037"></div>
              <div className={`h-12 rounded ${darkMode ? 'bg-navy-700' : 'bg-gray-200'} animate-pulse`} data-id="element-12038"></div>
              <div className={`h-24 rounded ${darkMode ? 'bg-navy-700' : 'bg-gray-200'} animate-pulse`} data-id="element-12039"></div>
            </div> : task ? <form onSubmit={handleSaveChanges} className="p-6 space-y-6" data-id="element-12040">
              {/* Assignee Section - Always read-only */}
              <div data-id="element-12041">
                <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-12042">
                  Assigned To
                </h3>
                <div className="flex items-center cursor-pointer group" onClick={() => console.log('open student profile', task.studentId)} data-id="element-12043">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center mr-3 ${darkMode ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-12044">
                    {task.studentAvatarUrl ? <img src={task.studentAvatarUrl} alt={task.studentName} className="h-10 w-10 rounded-full object-cover" data-id="element-12045" /> : <UserIcon className="h-5 w-5 text-[#3B82F6] group-hover:opacity-[1.15] transition-opacity" data-id="element-12046" />}
                  </div>
                  <div data-id="element-12047">
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'} group-hover:text-deep-pink transition-colors`} data-id="element-12048">
                      {task.studentName || 'Unknown Student'}
                    </p>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-12049">
                      Student ID: {task.studentId}
                    </p>
                  </div>
                </div>
              </div>
              {/* Title Section - Editable in edit mode */}
              {isEditMode && <div data-id="element-12050">
                  <label htmlFor="task-title" className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-12051">
                    Task Title <span className="text-red-500" data-id="element-12052">*</span>
                  </label>
                  <input type="text" id="task-title" name="title" value={formValues.title} onChange={handleInputChange} className={`w-full px-3 py-2 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-900 focus:ring-deep-pink focus:border-deep-pink'} border ${errors.title ? 'border-red-500' : ''}`} placeholder="Enter task title" data-id="element-12053" />
                  {errors.title && <p className="mt-1 text-xs text-red-500" data-id="element-12054">{errors.title}</p>}
                </div>}
              {/* Due Date & Status Section */}
              <div className={`pb-6 border-b ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-12055">
                <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-12056">
                  Task Timeline & Status
                </h3>
                <div className="flex flex-col space-y-4" data-id="element-12057">
                  {/* Due Date */}
                  {isEditMode ? <div className="space-y-3" data-id="element-12058">
                      <div data-id="element-12059">
                        <label htmlFor="task-due-date" className={`block text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-12060">
                          Due Date
                        </label>
                        <input type="date" id="task-due-date" name="dueDate" value={formValues.dueDate} onChange={handleInputChange} className={`w-full px-3 py-2 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-900 focus:ring-deep-pink focus:border-deep-pink'} border ${errors.dueDate ? 'border-red-500' : ''}`} data-id="element-12061" />
                        {errors.dueDate && <p className="mt-1 text-xs text-red-500" data-id="element-12062">
                            {errors.dueDate}
                          </p>}
                      </div>
                    </div> : <div className="flex items-start group" data-id="element-12063">
                      <CalendarIcon className="h-5 w-5 mt-0.5 mr-3 text-[#EF4444] group-hover:opacity-[1.15] transition-opacity" data-id="element-12064" />
                      <div data-id="element-12065">
                        <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-12066">
                          {isOverdue(getDueDate()) ? <span className="text-red-500" data-id="element-12067">Overdue</span> : 'Due Date'}
                        </p>
                        <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-12068">
                          {formatDateTime(getDueDate())}
                        </p>
                      </div>
                    </div>}
                  {/* Status - Editable in edit mode */}
                  {!isEditMode && <div className="flex items-center group" data-id="element-12069">
                      <ClipboardIcon className="h-5 w-5 mr-3 text-[#22C55E] group-hover:opacity-[1.15] transition-opacity" data-id="element-12070" />
                      <div data-id="element-12071">
                        <p className={`font-medium mb-1.5 ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-12072">
                          Status
                        </p>
                        <span className={`px-3 py-1 rounded-full text-sm ${getStatusBadge(undefined, task)}`} data-id="element-12073">
                          {getStatusText(undefined, task)}
                        </span>
                      </div>
                    </div>}
                </div>
              </div>
              {/* Notes Section - Editable in edit mode */}
              <div className={`pb-6 border-b ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-12074">
                <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-12075">
                  Notes
                </h3>
                {isEditMode ? <textarea id="task-notes" name="notes" value={formValues.notes} onChange={handleInputChange} rows={4} className={`w-full px-3 py-2 rounded-lg resize-none ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-900 focus:ring-deep-pink focus:border-deep-pink'} border`} placeholder="Add notes about this task (optional)" data-id="element-12076" /> : task.notes ? <div className={`whitespace-pre-line ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-12077">
                    {task.notes}
                  </div> : <p className={`italic ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-12078">
                    No notes added
                  </p>}
              </div>
              {/* Reference URL Field - Add in edit mode */}
              <div className={`pb-6 border-b ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-12079">
                <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-12080">
                  Reference
                </h3>
                {isEditMode ? <input type="text" id="task-reference" name="reference" value={formValues.reference} onChange={handleInputChange} className={`w-full px-3 py-2 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-900 focus:ring-deep-pink focus:border-deep-pink'} border`} placeholder="https://..." data-id="element-12081" /> : task.reference ? <a href={task.reference} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center ${darkMode ? 'text-deep-pink hover:text-burgundy' : 'text-deep-pink hover:text-burgundy'} transition-colors`} data-id="element-12082">
                    <ExternalLinkIcon className="h-4 w-4 mr-2" data-id="element-12083" />
                    Open reference link
                  </a> : <p className={`italic ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-12084">
                    No reference link added
                  </p>}
              </div>
              {/* Meta Section - Read-only */}
              {!isEditMode && <div data-id="element-12085">
                  <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-12086">
                    Details
                  </h3>
                  <div className="space-y-3" data-id="element-12087">
                    {task.createdAt && <div className="flex items-start group" data-id="element-12088">
                        <ClockIcon className="h-4 w-4 mt-0.5 mr-2 text-[#8B5CF6] group-hover:opacity-[1.15] transition-opacity" data-id="element-12089" />
                        <div data-id="element-12090">
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-12091">
                            Created
                          </p>
                          <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-12092">
                            {formatDateTime(task.createdAt)}
                          </p>
                        </div>
                      </div>}
                    <div className="flex items-start group" data-id="element-12093">
                      <div className="h-4 w-4 mt-0.5 mr-2 flex items-center justify-center text-[#8B5CF6] group-hover:opacity-[1.15] transition-opacity" data-id="element-12094">
                        #
                      </div>
                      <div className="flex-1" data-id="element-12095">
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-12096">
                          Task ID
                        </p>
                        <p className={`text-sm font-mono ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-12097">
                          {task.id}
                        </p>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-12098">
                        <button type="button" onClick={handleCopyTaskId} className={`p-1 rounded ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-100'} transition-colors`} title={isCopied ? 'Copied!' : 'Copy to clipboard'} data-id="element-12099">
                          {isCopied ? <CheckIcon className="h-3.5 w-3.5 text-green-500" data-id="element-12100" /> : <ClipboardIcon className="h-3.5 w-3.5" data-id="element-12101" />}
                        </button>
                        {isCopied && <span className="text-xs text-green-500 font-medium whitespace-nowrap animate-in fade-in duration-200" data-id="element-12102">
                            Task ID copied
                          </span>}
                      </div>
                    </div>
                  </div>
                </div>}
            </form> : <div className="p-6 text-center" data-id="element-12103">
              <p className={darkMode ? 'text-gray-400' : 'text-gray-500'} data-id="element-12104">
                No task data available
              </p>
            </div>}
        </div>
        {/* Panel Footer */}
        {!isLoading && task && <div className={`p-6 border-t ${darkMode ? 'border-navy-700' : 'border-gray-200'} flex justify-between`} data-id="element-12105">
            {isEditMode ? <>
                <button type="button" onClick={e => {
            e.stopPropagation();
            handleCancelEdit();
          }} className={`px-4 py-2 rounded-lg border ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-navy-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'} transition-colors flex items-center`} data-id="element-12106">
                  <XCircleIcon className="h-4 w-4 mr-2" data-id="element-12107" />
                  Cancel
                </button>
                <button type="button" onClick={e => {
            e.stopPropagation();
            handleSaveChanges(e);
          }} disabled={!formValues.title.trim()} className={`px-4 py-2 rounded-md border-2 flex items-center transition-colors ${!formValues.title.trim() ? 'border-gray-400 text-gray-400 cursor-not-allowed' : darkMode ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5'}`} data-id="element-12108">
                  <CheckIcon className="h-4 w-4 mr-1" data-id="element-12109" />
                  Save Changes
                </button>
              </> : <>
                <button type="button" onClick={e => {
            e.stopPropagation();
            setShowDeleteConfirm(true);
          }} className={`px-4 py-2 rounded-lg flex items-center ${darkMode ? 'text-red-400 hover:bg-red-900/20' : 'text-red-600 hover:bg-red-50'} transition-colors`} data-id="element-12110">
                  <TrashIcon className="h-4 w-4 mr-2" data-id="element-12111" />
                  Delete
                </button>
                <button type="button" onClick={handleEnterEditMode} className={`px-4 py-2 rounded-md border-2 flex items-center transition-colors ${darkMode ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5'}`} data-id="element-12112">
                  <PencilIcon className="h-4 w-4 mr-1" data-id="element-12113" />
                  Edit Task
                </button>
              </>}
          </div>}
      </div>
      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && <div className="fixed inset-0 z-60 overflow-y-auto" data-id="element-12114">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center" data-id="element-12115">
            <div className="fixed inset-0 transition-opacity" onClick={() => {
          setShowDeleteConfirm(false);
          setRecurringDeleteScope(null);
        }} data-id="element-12116">
              <div className={`absolute inset-0 ${darkMode ? 'bg-navy-900' : 'bg-gray-900'} opacity-75`} data-id="element-12117"></div>
            </div>
            <div className={`inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full ${darkMode ? 'bg-navy-800 text-white' : 'bg-white text-gray-800'}`} data-id="element-12118">
              <div className="p-6" data-id="element-12119">
                <h3 className="text-lg font-medium mb-4" data-id="element-12120">
                  Are you sure you want to delete this task?
                </h3>

                {task?.isRecurring ? <div className="space-y-3 mb-6" data-id="element-12121">
                    <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-12122">
                      This is a recurring task. How would you like to delete it?
                    </p>

                    {/* Option 1: Single */}
                    <label className={`flex items-start p-3 rounded-lg border cursor-pointer transition-colors ${recurringDeleteScope === 'single' ? darkMode ? 'border-[#10B981] bg-[#10B981]/10' : 'border-[#10B981] bg-[#10B981]/5' : darkMode ? 'border-navy-600 hover:bg-navy-700/50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-12123">
                      <div className="flex items-center h-5" data-id="element-12124">
                        <input type="radio" name="deleteScope" value="single" checked={recurringDeleteScope === 'single'} onChange={() => setRecurringDeleteScope('single')} className="h-4 w-4 text-[#10B981] focus:ring-[#10B981] border-gray-300" data-id="element-12125" />
                      </div>
                      <div className="ml-3 flex-1" data-id="element-12126">
                        <span className={`block text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-12127">
                          This task only
                        </span>
                        <span className={`block text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-12128">
                          Only delete the task scheduled for{' '}
                          {formatDate(task.dueDate || task.deadline || task.dueAt)}
                        </span>
                      </div>
                    </label>

                    {/* Option 2: Future */}
                    <label className={`flex items-start p-3 rounded-lg border cursor-pointer transition-colors ${recurringDeleteScope === 'future' ? darkMode ? 'border-[#10B981] bg-[#10B981]/10' : 'border-[#10B981] bg-[#10B981]/5' : darkMode ? 'border-navy-600 hover:bg-navy-700/50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-12129">
                      <div className="flex items-center h-5" data-id="element-12130">
                        <input type="radio" name="deleteScope" value="future" checked={recurringDeleteScope === 'future'} onChange={() => setRecurringDeleteScope('future')} className="h-4 w-4 text-[#10B981] focus:ring-[#10B981] border-gray-300" data-id="element-12131" />
                      </div>
                      <div className="ml-3 flex-1" data-id="element-12132">
                        <span className={`block text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-12133">
                          This and all future tasks
                        </span>
                        <span className={`block text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-12134">
                          Delete this task and all subsequent tasks in the
                          series
                        </span>
                      </div>
                    </label>

                    {/* Option 3: All */}
                    <label className={`flex items-start p-3 rounded-lg border cursor-pointer transition-colors ${recurringDeleteScope === 'all' ? darkMode ? 'border-[#10B981] bg-[#10B981]/10' : 'border-[#10B981] bg-[#10B981]/5' : darkMode ? 'border-navy-600 hover:bg-navy-700/50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-12135">
                      <div className="flex items-center h-5" data-id="element-12136">
                        <input type="radio" name="deleteScope" value="all" checked={recurringDeleteScope === 'all'} onChange={() => setRecurringDeleteScope('all')} className="h-4 w-4 text-[#10B981] focus:ring-[#10B981] border-gray-300" data-id="element-12137" />
                      </div>
                      <div className="ml-3 flex-1" data-id="element-12138">
                        <span className={`block text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-12139">
                          All tasks in the series
                        </span>
                        <span className={`block text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-12140">
                          Delete every task in this recurring series, including
                          past ones
                        </span>
                      </div>
                    </label>
                  </div> : <p className={`text-sm mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-12141">
                    This action cannot be undone. This will permanently delete
                    the task.
                  </p>}

                <div className="flex justify-end space-x-3" data-id="element-12142">
                  <button type="button" onClick={() => {
                setShowDeleteConfirm(false);
                setRecurringDeleteScope(null);
              }} className={`px-4 py-2 rounded-lg border ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-navy-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'} transition-colors`} data-id="element-12143">
                    Cancel
                  </button>
                  <button type="button" onClick={handleDeleteConfirm} disabled={task?.isRecurring && !recurringDeleteScope} className={`px-4 py-2 rounded-lg text-white transition-colors ${task?.isRecurring && !recurringDeleteScope ? 'bg-red-400 cursor-not-allowed opacity-50' : 'bg-red-600 hover:bg-red-700'}`} data-id="element-12144">
                    Yes, Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Recurring Edit Scope Modal */}
      {recurringEditScope !== null && <div className="fixed inset-0 z-60 overflow-y-auto" data-id="element-12145">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center" data-id="element-12146">
            <div className="fixed inset-0 transition-opacity" onClick={() => setRecurringEditScope(null)} data-id="element-12147">
              <div className={`absolute inset-0 ${darkMode ? 'bg-navy-900' : 'bg-gray-900'} opacity-75`} data-id="element-12148"></div>
            </div>
            <div className={`inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full ${darkMode ? 'bg-navy-800 text-white' : 'bg-white text-gray-800'}`} data-id="element-12149">
              <div className="p-6" data-id="element-12150">
                <h3 className="text-lg font-medium mb-4" data-id="element-12151">
                  Edit recurring task
                </h3>
                <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-12152">
                  This is a recurring task. How would you like to apply your
                  changes?
                </p>

                <div className="space-y-3 mb-6" data-id="element-12153">
                  {/* Option 1: Single */}
                  <label className={`flex items-start p-3 rounded-lg border cursor-pointer transition-colors ${recurringEditScope === 'single' ? darkMode ? 'border-[#10B981] bg-[#10B981]/10' : 'border-[#10B981] bg-[#10B981]/5' : darkMode ? 'border-navy-600 hover:bg-navy-700/50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-12154">
                    <div className="flex items-center h-5" data-id="element-12155">
                      <input type="radio" name="editScope" value="single" checked={recurringEditScope === 'single'} onChange={() => setRecurringEditScope('single')} className="h-4 w-4 text-[#10B981] focus:ring-[#10B981] border-gray-300" data-id="element-12156" />
                    </div>
                    <div className="ml-3 flex-1" data-id="element-12157">
                      <span className={`block text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-12158">
                        This task only
                      </span>
                      <span className={`block text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-12159">
                        Only update the task scheduled for{' '}
                        {formatDate(task?.dueDate || task?.deadline || task?.dueAt)}
                      </span>
                    </div>
                  </label>

                  {/* Option 2: Future */}
                  <label className={`flex items-start p-3 rounded-lg border cursor-pointer transition-colors ${recurringEditScope === 'future' ? darkMode ? 'border-[#10B981] bg-[#10B981]/10' : 'border-[#10B981] bg-[#10B981]/5' : darkMode ? 'border-navy-600 hover:bg-navy-700/50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-12160">
                    <div className="flex items-center h-5" data-id="element-12161">
                      <input type="radio" name="editScope" value="future" checked={recurringEditScope === 'future'} onChange={() => setRecurringEditScope('future')} className="h-4 w-4 text-[#10B981] focus:ring-[#10B981] border-gray-300" data-id="element-12162" />
                    </div>
                    <div className="ml-3 flex-1" data-id="element-12163">
                      <span className={`block text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-12164">
                        This and all future tasks
                      </span>
                      <span className={`block text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-12165">
                        Update this task and all subsequent tasks in the series
                      </span>
                    </div>
                  </label>

                  {/* Option 3: All */}
                  <label className={`flex items-start p-3 rounded-lg border cursor-pointer transition-colors ${recurringEditScope === 'all' ? darkMode ? 'border-[#10B981] bg-[#10B981]/10' : 'border-[#10B981] bg-[#10B981]/5' : darkMode ? 'border-navy-600 hover:bg-navy-700/50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-12166">
                    <div className="flex items-center h-5" data-id="element-12167">
                      <input type="radio" name="editScope" value="all" checked={recurringEditScope === 'all'} onChange={() => setRecurringEditScope('all')} className="h-4 w-4 text-[#10B981] focus:ring-[#10B981] border-gray-300" data-id="element-12168" />
                    </div>
                    <div className="ml-3 flex-1" data-id="element-12169">
                      <span className={`block text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-12170">
                        All tasks in the series
                      </span>
                      <span className={`block text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-12171">
                        Update every task in this recurring series, including
                        past ones
                      </span>
                    </div>
                  </label>
                </div>

                <div className="flex justify-end space-x-3" data-id="element-12172">
                  <button type="button" onClick={() => setRecurringEditScope(null)} className={`px-4 py-2 rounded-lg border ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-navy-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'} transition-colors`} data-id="element-12173">
                    Cancel
                  </button>
                  <button type="button" onClick={e => handleSaveChanges(e)} disabled={isSaving} className={`px-4 py-2 rounded-lg text-white transition-colors ${isSaving ? 'bg-[#10B981]/50 cursor-not-allowed' : 'bg-[#10B981] hover:bg-[#0D9668]'}`} data-id="element-12174">
                    {isSaving ? 'Saving...' : 'Apply Changes'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};